// Stable className strings backed by globally-injected CSS. Same pattern as
// `global/styles/filters.js` — preserves the `useStyles(props).<key>` consumer
// API without needing @mui/styles. The width/height/opacity variants that the
// original makeStyles computed at runtime are exposed as variant class strings
// that we toggle from the returned object based on hook args.
import { alpha, useTheme } from '@mui/material/styles';
import { screenDims } from '../../styles';

const PREFIX = 'globalDroprealms';
const mobileWidth = '100%';
const dims = screenDims();
const formWidth = 500;

let injectedHash = null;
const themeHash = (theme) =>
  `${theme.palette.primary.main}|${theme.palette.secondary.main}|${theme.palette.success.main}`;
const injectStyles = (theme) => {
  if (typeof document === 'undefined') return;
  const hash = themeHash(theme);
  if (injectedHash === hash) return;
  const prev = document.getElementById(`${PREFIX}-styles`);
  if (prev) prev.remove();
  injectedHash = hash;

  const css = `
    .${PREFIX}-container {}
    .${PREFIX}-modal { height: 100%; }

    .${PREFIX}-titleRow {
      display: flex;
      flex-direction: row;
      align-items: center;
      flex-wrap: wrap;
    }
    .${PREFIX}-titleRow .realmTitle { font-size: 13px; }
    .${PREFIX}-titleRow .realmTitle svg { margin-right: 6px; margin-top: -2px; }
    .${PREFIX}-titleRow .realmTitle .spacer { margin: 0 ${theme.spacing(1)}; }
    .${PREFIX}-titleRow.selectTitleRow {
      text-align: center;
      padding: ${theme.spacing(3)};
      padding-top: 0;
      width: 300px;
      align-self: center;
      margin: 0 auto;
      align-items: center;
      justify-content: center;
      display: flex;
      flex-direction: row;
    }
    .${PREFIX}-titleRow.selectTitleRow svg { margin: -4px 5px; }

    .${PREFIX}-greenText {
      font-size: 14px;
      color: ${theme.palette.success.main};
      font-weight: bold;
    }
    .${PREFIX}-title { font-size: 12px; }
    .${PREFIX}-firstItem { padding: 6px 0 7px; }
    .${PREFIX}-toggleButton {
      color: ${theme.palette.secondary.main};
      margin-left: ${theme.spacing(2)};
    }
    .${PREFIX}-toggleButtonRealmChoice {
      color: ${theme.palette.primary.main};
      cursor: pointer;
      font-size: .75rem;
      white-space: nowrap;
      min-width: auto;
    }
    .${PREFIX}-tinyMarginTop { margin-top: 4px; }
    .${PREFIX}-smallMarginTop { margin-top: 6px; }
    .${PREFIX}-fixedWidth { width: 300px; }
    .${PREFIX}-marginLeft { margin-left: ${theme.spacing(3)}; }
    .${PREFIX}-realmText { margin-left: ${theme.spacing(1)}; }
    .${PREFIX}-realmRowIcon { padding: ${theme.spacing(1)}; }

    .${PREFIX}-listWrap { margin: 0 auto; width: ${formWidth}px; padding: 0 ${theme.spacing(6)}; }
    .${PREFIX}-listWrap-mobile { width: ${mobileWidth}; padding: 0; }

    .${PREFIX}-listText {
      font-size: 13px;
      padding-top: ${theme.spacing(1)};
      padding-bottom: 0;
    }
    .${PREFIX}-listText svg { font-size: 18px; }
    .${PREFIX}-listText .realmTitleRow { padding-bottom: ${theme.spacing(1)}; }
    .${PREFIX}-listText .titleDiv { margin: ${theme.spacing(1)} 0; }

    .${PREFIX}-list {
      height: inherit;
      width: ${formWidth}px;
      overflow-x: hidden;
      overflow: auto;
      border-top: 1px solid ${theme.palette.action.disabledBackground};
      padding: 0;
      opacity: 1;
    }
    .${PREFIX}-list-mobile { height: ${dims.height - 200}px; width: ${mobileWidth}; }
    .${PREFIX}-list-loading { opacity: 0.7; }

    .${PREFIX}-listItemRow {
      width: ${formWidth}px;
      height: 43px;
      cursor: pointer;
      padding: 0;
      border-bottom: 1px solid ${theme.palette.action.disabledBackground};
      pointer-events: all;
    }
    .${PREFIX}-listItemRow-mobile { width: ${mobileWidth}; }
    .${PREFIX}-listItemRow-loading { pointer-events: none; }
    .${PREFIX}-listItemRow.backIcon > div {
      margin-left: 47px;
      margin-right: 0;
      background-color: transparent;
    }
    .${PREFIX}-listItemRow.backIcon .MuiListItemText-root { margin-left: 0; }
    .${PREFIX}-listItemRow .keyText { font-size: 12px; align-self: flex-start; }

    .${PREFIX}-listItemRowWrapper {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: row;
      align-items: center;
      padding-left: ${theme.spacing(3)};
    }
    .${PREFIX}-listItemRowWrapper:hover { background-color: ${theme.palette.action.hover}; }

    .${PREFIX}-listRowIcon {
      background-color: ${alpha(theme.palette.action.disabledBackground, 0.05)};
      height: 100%;
      width: 55px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: ${theme.palette.secondary.main};
    }
    .${PREFIX}-listRowIcon:hover { background-color: ${alpha(theme.palette.action.disabledBackground, 0.1)}; }
    .${PREFIX}-listRowIconSelected { background-color: ${theme.palette.action.disabledBackground}; }
    .${PREFIX}-listRowIconDisabled .MuiIcon-root, .${PREFIX}-listRowIconDisabled .MuiSvgIcon-root {
      color: ${theme.palette.action.disabledBackground};
    }

    .${PREFIX}-notSelectable { pointer-events: none; }
    .${PREFIX}-notSelectable .MuiIcon-root, .${PREFIX}-notSelectable .MuiSvgIcon-root {
      color: ${theme.palette.action.disabledBackground};
    }
    .${PREFIX}-listItemRowEdit { height: auto; }
    .${PREFIX}-isSelected { background-color: ${theme.palette.action.disabledBackground}; }
    .${PREFIX}-defaultWhite { background-color: transparent; }

    .${PREFIX}-secondaryAction { right: 0; pointer-events: none; }
    .${PREFIX}-secondaryAction button { padding: 9px; }
    .${PREFIX}-secondaryActionIcon { color: ${theme.palette.text.primary}; }

    .${PREFIX}-editAction { margin-left: -17px; flex: none; }
    .${PREFIX}-editAction button { padding: 9px; }

    .${PREFIX}-itemEditContainer {
      display: flex;
      flex-direction: column;
      flex: 1;
      padding: ${theme.spacing(1)};
    }
    .${PREFIX}-editRow { display: flex; flex-direction: row; }
    .${PREFIX}-editButtonRow {
      display: flex;
      flex-direction: row;
      margin-top: ${theme.spacing(1)};
      justify-content: flex-end;
    }
    .${PREFIX}-editButtonRow button { margin-left: ${theme.spacing(1)}; }

    .${PREFIX}-editButton { background-color: ${theme.palette.secondary.main}; }
    .${PREFIX}-editCancel { background-color: ${theme.palette.action.disabled}; }

    .${PREFIX}-archiveRow {
      background-color: ${alpha(theme.palette.error.main, 0.2)};
      display: flex;
      flex-direction: row;
      padding: ${theme.spacing(1)};
      padding-left: ${theme.spacing(2)};
      margin-bottom: ${theme.spacing(2)};
      align-items: center;
    }
    .${PREFIX}-archiveText {
      display: flex;
      flex-direction: row;
      flex: 1;
      font-size: 12px;
    }
    .${PREFIX}-archiveButtonRow button { margin-left: ${theme.spacing(1)}; }
  `;

  const tag = document.createElement('style');
  tag.id = `${PREFIX}-styles`;
  tag.textContent = css;
  document.head.appendChild(tag);
};

const baseClasses = {
  container: `${PREFIX}-container`,
  modal: `${PREFIX}-modal`,
  titleRow: `${PREFIX}-titleRow`,
  greenText: `${PREFIX}-greenText`,
  title: `${PREFIX}-title`,
  firstItem: `${PREFIX}-firstItem`,
  toggleButton: `${PREFIX}-toggleButton`,
  toggleButtonRealmChoice: `${PREFIX}-toggleButtonRealmChoice`,
  tinyMarginTop: `${PREFIX}-tinyMarginTop`,
  smallMarginTop: `${PREFIX}-smallMarginTop`,
  fixedWidth: `${PREFIX}-fixedWidth`,
  marginLeft: `${PREFIX}-marginLeft`,
  realmText: `${PREFIX}-realmText`,
  realmRowIcon: `${PREFIX}-realmRowIcon`,
  listText: `${PREFIX}-listText`,
  listItemRowWrapper: `${PREFIX}-listItemRowWrapper`,
  listRowIcon: `${PREFIX}-listRowIcon`,
  listRowIconSelected: `${PREFIX}-listRowIconSelected`,
  listRowIconDisabled: `${PREFIX}-listRowIconDisabled`,
  notSelectable: `${PREFIX}-notSelectable`,
  listItemRowEdit: `${PREFIX}-listItemRowEdit`,
  isSelected: `${PREFIX}-isSelected`,
  defaultWhite: `${PREFIX}-defaultWhite`,
  secondaryAction: `${PREFIX}-secondaryAction`,
  secondaryActionIcon: `${PREFIX}-secondaryActionIcon`,
  editAction: `${PREFIX}-editAction`,
  itemEditContainer: `${PREFIX}-itemEditContainer`,
  editRow: `${PREFIX}-editRow`,
  editButtonRow: `${PREFIX}-editButtonRow`,
  editButton: `${PREFIX}-editButton`,
  editCancel: `${PREFIX}-editCancel`,
  archiveRow: `${PREFIX}-archiveRow`,
  archiveText: `${PREFIX}-archiveText`,
  archiveButtonRow: `${PREFIX}-archiveButtonRow`,
};

export const useDropStyles = (opts = {}) => {
  const theme = useTheme();
  injectStyles(theme);
  return {
    ...baseClasses,
    listWrap: opts.isMobile ? `${PREFIX}-listWrap ${PREFIX}-listWrap-mobile` : `${PREFIX}-listWrap`,
    list: [
      `${PREFIX}-list`,
      opts.isMobile && `${PREFIX}-list-mobile`,
      opts.loading && `${PREFIX}-list-loading`,
    ].filter(Boolean).join(' '),
    listItemRow: [
      `${PREFIX}-listItemRow`,
      opts.isMobile && `${PREFIX}-listItemRow-mobile`,
      opts.loading && `${PREFIX}-listItemRow-loading`,
    ].filter(Boolean).join(' '),
  };
};

// Re-export under the original name for backward compatibility.
export const dropStyles = useDropStyles;
export default useDropStyles;
